// src/components/HowAIWorksView.tsx
//
// New page — /how-ai-works (or wherever routed). Built from the
// session's AI-vs-protocol-vs-trust taxonomy (buildlog 9oYeT1uujfYUIHG0hRH6):
// only Steps 3 (scoping) and 4 (negotiation/drafting) are genuine LLM
// inference. Step 5 (detection) is confirmed, from direct read of
// run-marqeta-detection.ts, to be statistical classification — zero
// model calls. This page states that distinction plainly rather than
// letting "AI checks everything" stand uncorrected.
//
// UPDATED — Section 3 now renders the real ApprovalGateDemo component
// (built from the actual Bubble/Marker/Button shadcn primitives, a
// faithful recreation of ChatScreen.tsx's PendingApprovalCard
// mechanism), not a placeholder comment. Simulated interaction only —
// no real resolveApproval call — the surrounding copy says so
// explicitly rather than implying a live session.
//
// Immutability language deliberately softened — get_track_record's
// "not self-reported" claim is evidenced by its tool description, not
// by a confirmed write-once/hash-chain mechanism in code. Avoiding
// "never edited after" as an unqualified claim until that's checked.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from './SEO';
import { Card, CardContent } from './ui/card';
import { ConnectActions } from './ConnectActions';
import { ApprovalGateDemo } from './ApprovalGateDemo';
import { BrainCircuit, BarChart3, ShieldCheck } from 'lucide-react';

export const HowAIWorksView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Five Dollar Workday | Where the AI Ends and the Proof Begins"
        description="An AI system drafts and understands. A statistical model checks the facts against your own history. Neither one closes a case without a human. See exactly where each one runs."
        canonical="https://fivedollarworkday.com/how-it-works"
      />

      <main className="relative min-h-screen bg-brand-surface text-brand-black font-sans selection:bg-brand-accent selection:text-white">

        {/* ── 1. HERO ─────────────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-24 md:py-32 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-2xl flex flex-col gap-6">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray">
                How it actually works
              </p>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] text-brand-black">
                Where the AI ends and the proof begins.
              </h1>
              <p className="text-lg text-brand-charcoal font-light leading-relaxed">
                An AI system drafts and understands what you need. A statistical model checks the
                facts against your own history. Neither one closes a case without you.
              </p>
            </div>
          </div>
        </section>

        {/* ── 2. THE THREE-PART SYSTEM ────────────────────────────── */}
        <section className="px-6 md:px-12 py-24 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="mb-10 max-w-2xl">
              <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray mb-2">
                Three parts, not one
              </p>
              <h2 className="text-3xl font-semibold tracking-tight text-brand-black">
                Most of the work isn't one thing pretending to be AI.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              <Card className="border border-brand-border rounded-2xl bg-white">
                <CardContent className="p-6 flex flex-col gap-3">
                  <BrainCircuit className="w-6 h-6 text-brand-black" strokeWidth={1.5} />
                  <p className="text-base font-medium text-brand-black">What the AI does</p>
                  <p className="text-sm text-brand-charcoal font-light leading-relaxed">
                    Understands what you're asking for, and drafts a reply. This is genuine model
                    inference — the two places in the whole process where it actually runs.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-brand-border rounded-2xl bg-white">
                <CardContent className="p-6 flex flex-col gap-3">
                  <BarChart3 className="w-6 h-6 text-brand-black" strokeWidth={1.5} />
                  <p className="text-base font-medium text-brand-black">What the statistics do</p>
                  <p className="text-sm text-brand-charcoal font-light leading-relaxed">
                    Checks every transaction against your own historical baseline — not a generic
                    threshold, and not a model guessing. A number, compared to a number.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-brand-border rounded-2xl bg-white">
                <CardContent className="p-6 flex flex-col gap-3">
                  <ShieldCheck className="w-6 h-6 text-brand-black" strokeWidth={1.5} />
                  <p className="text-base font-medium text-brand-black">What stays human, always</p>
                  <p className="text-sm text-brand-charcoal font-light leading-relaxed">
                    Approving anything before it sends. Writing the note that says a finding was
                    actually reviewed. Nothing here is automated away — on purpose.
                  </p>
                </CardContent>
              </Card>

            </div>
          </div>
        </section>

        {/* ── 3. THE MOMENT OF TRUTH — real approval-gate demo ────── */}
        <section className="px-6 md:px-12 py-24 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="mb-10 max-w-2xl">
              <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray mb-2">
                See it, don't just take our word for it
              </p>
              <h2 className="text-3xl font-semibold tracking-tight text-brand-black">
                When something needs your sign-off, the interface stops.
              </h2>
              <p className="text-brand-charcoal font-light leading-relaxed mt-4">
                Not as a policy. As a fact you can watch happen. This is a demonstration of the
                real mechanism our own dashboard uses — try declining, then approving.
              </p>
            </div>

            <ApprovalGateDemo />

          </div>
        </section>

        {/* ── 4. WHY DETECTION ISN'T "AI GUESSING" ────────────────── */}
        <section className="px-6 md:px-12 py-24 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-2xl">
              <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray mb-2">
                The honest version
              </p>
              <h2 className="text-3xl font-semibold tracking-tight text-brand-black mb-6">
                Why detection isn't "AI guessing."
              </h2>
              <div className="space-y-4">
                <p className="text-brand-charcoal text-base leading-relaxed font-light">
                  A model that explains why a transaction looks unusual can sound completely
                  convincing and still be wrong — there's no number underneath the sentence to
                  check. That's not good enough for an audit trail.
                </p>
                <p className="text-brand-charcoal text-base leading-relaxed font-light">
                  So detection doesn't work that way. Every transaction is checked against your
                  own historical baseline for that merchant and category — a real delta, a real
                  sample size, a real threshold. The reason a finding gets flagged is a number you
                  can verify yourself, not a paragraph you have to trust.
                </p>
                <p className="text-brand-charcoal text-base leading-relaxed font-light">
                  That's a deliberate choice, not a limitation. For the transactions that need to
                  hold up under a real audit, provable is worth more than clever.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 5. WHAT WE WON'T AUTOMATE ───────────────────────────── */}
        <section className="px-6 md:px-12 py-24 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-2xl">
              <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray mb-2">
                On purpose, not by accident
              </p>
              <h2 className="text-3xl font-semibold tracking-tight text-brand-black mb-6">
                What we won't automate — and why.
              </h2>
              <div className="space-y-4">
                <p className="text-brand-charcoal text-base leading-relaxed font-light">
                  When a finding needs a human decision, we don't auto-draft the resolution and
                  ask you to click accept. You write what you actually found — a real account, not
                  a placeholder. The system won't let you submit "done" and move on.
                </p>
                <p className="text-brand-charcoal text-base leading-relaxed font-light">
                  Every completed engagement writes an entry to a record — the same one the next
                  agent checking you out will read. It's written by the platform at the moment of
                  completion, not typed up afterward by the entity being reviewed.
                </p>
                <p className="text-brand-charcoal text-sm leading-relaxed font-light pt-2 border-t border-brand-border">
                  Speed is easy to add later. Trust is not — so we didn't trade it away to get
                  there faster.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 6. CTA ───────────────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-24">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-lg flex flex-col gap-6">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-black">
                See it running on a real engagement.
              </h2>
              <p className="text-brand-charcoal font-light">
                Sign up, or message us directly — same door either way.
              </p>
              <ConnectActions onSignUpClick={() => navigate('/login')} />
            </div>
          </div>
        </section>

      </main>
    </>
  );
};